import React from "react";
import { FaRedo } from "react-icons/fa";

import useScrollToTop from "../../../hooks/useScollToTop";

import AdminDashboardStatsGrid from "./components/AdminDashboardStatsGrid";
import AdminMessagesPanel from "./components/AdminMessagesPanel";
import AdminLineChartCard from "./components/AdminLineChartCard";
import VisitsChartCard from "./components/VisitsChartCard";
import SubscriptionBreakdownCard from "./components/SubscriptionBreakdownCard";
import DepartmentTableCard from "./components/DepartmentTableCard";
import AdminDashboardSkeleton from "./components/AdminDashboardSkeleton";
import AdminDashboardEmptyState from "./components/AdminDashboardEmptyState";

import { useAdminDashboard } from "./hooks/useAdminDashboard";

function Dashboard() {
  useScrollToTop();

  const {
    userStats,
    eventStats,
    kpis,
    messages,
    visitsData,
    adherentsGrowthData,
    departmentData,
    subscriptionsData,
    visitDeleteForm,
    loading,
    refreshing,
    deletingVisits,
    messageActionId,
    fetchDashboard,
    handleVisitDeleteFormChange,
    handleDeleteVisits,
    handleToggleMessageRead,
    handleDeleteMessage,
  } = useAdminDashboard();

  const hasData =
    kpis.totalUsers > 0 ||
    kpis.totalAdherents > 0 ||
    kpis.totalEvents > 0 ||
    messages.length > 0 ||
    visitsData.length > 0;

  return (
    <div className="w-full space-y-6 font-satoshi">
      <div className="flex flex-col gap-4 rounded-[2rem] border border-slate-200 bg-white p-5 shadow-xl shadow-slate-200/60 dark:border-white/10 dark:bg-slate-900 dark:shadow-black/20 xl:flex-row xl:items-center xl:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-sky-700 dark:text-sky-200">
            Centre de pilotage
          </p>

          <h2 className="mt-2 font-titre text-3xl font-bold text-slate-950 dark:text-white">
            Vue globale de l’application
          </h2>

          <p className="mt-2 max-w-3xl text-sm font-medium leading-6 text-slate-500 dark:text-white/50">
            Suivi des utilisateurs, adhérents, groupes, événements, messages,
            visites et abonnements.
          </p>
        </div>

        <button
          type="button"
          onClick={() => fetchDashboard({ silent: false })}
          disabled={refreshing}
          className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-700 transition hover:-translate-y-0.5 hover:border-sky-200 hover:text-sky-700 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:bg-white/[0.05] dark:text-white"
        >
          <FaRedo className={refreshing ? "animate-spin" : ""} />
          {refreshing ? "Actualisation..." : "Actualiser"}
        </button>
      </div>

      {loading ? (
        <AdminDashboardSkeleton />
      ) : !hasData ? (
        <AdminDashboardEmptyState
          onRefresh={() => fetchDashboard({ silent: false })}
        />
      ) : (
        <>
          <AdminDashboardStatsGrid
            kpis={kpis}
            userStats={userStats}
            eventStats={eventStats}
          />

          <div className="grid gap-5 xl:grid-cols-[0.75fr_1.25fr]">
            <AdminMessagesPanel
              messages={messages}
              actionId={messageActionId}
              onToggleRead={handleToggleMessageRead}
              onDelete={handleDeleteMessage}
            />

            <SubscriptionBreakdownCard data={subscriptionsData} />
          </div>

          <div className="grid gap-5 xl:grid-cols-2">
            <VisitsChartCard
              data={visitsData}
              form={visitDeleteForm}
              deleting={deletingVisits}
              onChange={handleVisitDeleteFormChange}
              onDelete={handleDeleteVisits}
            />

            <AdminLineChartCard
              title="Évolution des adhérents"
              subtitle="Progression cumulée du nombre d’adhérents dans la base."
              data={adherentsGrowthData}
              dataKey="count"
              valueLabel="Adhérents"
              gradientId="adherentsGradient"
              emptyText="Aucun adhérent enregistré."
            />
          </div>

          <DepartmentTableCard data={departmentData} />
        </>
      )}
    </div>
  );
}

export default Dashboard;